import { X } from "lucide-react";
import { getDownloadURL, ref } from "firebase/storage";
import { useEffect, useState, type ReactNode } from "react";
import { storage } from "../firebase";
import { classNames } from "../utils";

export function PageHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return <header className="page-header"><div><h1>{title}</h1>{description && <p>{description}</p>}</div>{action && <div className="page-actions">{action}</div>}</header>;
}

export function Card({ children, className, title, action }: { children: ReactNode; className?: string; title?: string; action?: ReactNode }) {
  return <section className={classNames("card", className)}>{(title || action) && <div className="card-header"><h2>{title}</h2>{action}</div>}{children}</section>;
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: string }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return <div className="empty-state"><div className="empty-illustration">✦</div><h3>{title}</h3><p>{body}</p>{action}</div>;
}

export function LoadingRows({ rows = 5 }: { rows?: number }) {
  return <div className="loading-rows">{Array.from({ length: rows }, (_, index) => <div className="skeleton" key={index} />)}</div>;
}

export function Modal({ open, title, subtitle, onClose, children, wide = false }: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  if (!open) return null;
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section className={classNames("modal", wide && "wide")} role="dialog" aria-modal="true" aria-label={title}>
      <header><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div><button className="icon-button" onClick={onClose} aria-label="Затвори"><X size={20} /></button></header>
      <div className="modal-content">{children}</div>
    </section>
  </div>;
}

export function ConfirmDialog({ open, title, body, confirmLabel = "Изтрий", danger = true, onCancel, onConfirm }: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  return <Modal open={open} title={title} onClose={onCancel}><p className="confirm-copy">{body}</p><div className="modal-actions"><button className="button secondary" onClick={onCancel}>Отказ</button><button className={classNames("button", danger ? "danger" : "primary")} onClick={onConfirm}>{confirmLabel}</button></div></Modal>;
}

export function Toast({ message, tone = "success", onClose }: { message: string; tone?: "success" | "error"; onClose: () => void }) {
  return <div className={`toast ${tone}`}><span>{message}</span><button onClick={onClose}><X size={16} /></button></div>;
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (value: boolean) => void; label?: string }) {
  return <label className="toggle-row"><button type="button" role="switch" aria-checked={checked} className={classNames("toggle", checked && "on")} onClick={() => onChange(!checked)}><span /></button>{label && <span>{label}</span>}</label>;
}

export function StorageImage({ path, alt = "", className }: { path?: string; alt?: string; className?: string }) {
  const [url, setUrl] = useState(/^https?:\/\//i.test(path ?? "") ? path ?? "" : "");
  useEffect(() => {
    let active = true;
    if (!path) {
      setUrl("");
      return;
    }
    if (/^https?:\/\//i.test(path)) {
      setUrl(path);
      return;
    }
    getDownloadURL(ref(storage, path)).then((nextUrl) => active && setUrl(nextUrl)).catch(() => active && setUrl(""));
    return () => { active = false; };
  }, [path]);
  return url ? <img className={className} src={url} alt={alt} /> : <div className={classNames("image-placeholder", className)}>M</div>;
}
